import React from 'react';
import { Link } from 'react-router-dom';

// Two-row infinite marquee. Each row scrolls in opposite directions.
// Photos live at /images/community/c1.jpg … c10.jpg.
// The component is content-agnostic — it duplicates the list inline so the
// CSS animation produces a seamless loop. Pauses on hover. No JS scroll.

// Photos prioritised so women-forward shots lead each row.
const photos = [
  '/images/community/c11.jpg',
  '/images/community/c5.jpg',
  '/images/community/c3.jpg',
  '/images/community/c2.jpg',
  '/images/community/c1.jpg',
  '/images/community/c4.jpg',
  '/images/community/c12.jpg',
  '/images/community/c9.jpg',
  '/images/community/c6.jpg',
  '/images/community/c7.jpg',
  '/images/community/c10.jpg',
  '/images/community/c8.jpg',
];

// Two rows: 6/6 split so they look different.
const rowA = photos.slice(0, 6);
const rowB = photos.slice(6);

const Row = ({ items, direction = 'left', duration = 60 }) => {
  // Duplicate items so the keyframe can scroll a full set out and the dup in.
  const doubled = [...items, ...items];
  return (
    <div
      className="relative overflow-hidden"
      style={{ maskImage: 'linear-gradient(90deg, transparent, black 6%, black 94%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, black 6%, black 94%, transparent)' }}
    >
      <div
        className="flex gap-3 md:gap-4 group-hover:[animation-play-state:paused]"
        style={{
          animation: `${direction === 'left' ? 'community-scroll-left' : 'community-scroll-right'} ${duration}s linear infinite`,
          width: 'max-content',
        }}
      >
        {doubled.map((src, i) => (
          <div
            key={i}
            className="relative shrink-0 w-72 sm:w-80 md:w-[22rem] aspect-[3/2] overflow-hidden bg-dark-green/10"
          >
            <img
              src={src}
              alt=""
              loading="lazy"
              draggable="false"
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const CommunityMarquee = () => (
  <section className="bg-cream py-14 sm:py-18 md:py-22 border-t border-dark-green/10">
    <style>{`
      @keyframes community-scroll-left {
        0%   { transform: translate3d(0, 0, 0); }
        100% { transform: translate3d(-50%, 0, 0); }
      }
      @keyframes community-scroll-right {
        0%   { transform: translate3d(-50%, 0, 0); }
        100% { transform: translate3d(0, 0, 0); }
      }
    `}</style>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8 md:mb-12">
      <div className="grid md:grid-cols-12 gap-4 md:gap-8 items-end">
        <div className="md:col-span-7">
          <p className="text-dark-green/60 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
            <span className="text-dark-green/40">&rarr;</span> 04 / The community
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-dark-green leading-tight">
            Extraordinary people<span className="text-dark-green/45"> doing extraordinary things.</span>
          </h2>
        </div>
        <div className="md:col-span-5 flex flex-col md:items-end gap-3">
          <p className="text-dark-green/60 text-sm md:text-base font-mono uppercase tracking-tight md:text-right">
            Moments from across our gatherings &middot; London
          </p>
          <Link
            to="/events"
            data-testid="community-events-cta"
            className="inline-flex items-center gap-2 bg-dark-green text-cream px-5 py-2.5 rounded-full text-sm sm:text-base font-medium hover:bg-dark-green/90 transition-all duration-300 group/btn"
          >
            See upcoming events
            <span aria-hidden className="group-hover/btn:translate-x-1 transition-transform">&rarr;</span>
          </Link>
        </div>
      </div>
    </div>

    {/* Edge-to-edge marquee, two rows, opposite directions */}
    <div data-testid="community-marquee" className="group space-y-3 md:space-y-4">
      <Row items={rowA} direction="left" duration={70} />
      <Row items={rowB} direction="right" duration={85} />
    </div>
  </section>
);

export default CommunityMarquee;
